import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as SentryAngular from '@sentry/angular';
import * as SentryCapacitor from '@sentry/capacitor';
import { AppflowLiveUpdatesService } from '@services/appflow-live-updates/appflow-live-updates.service';
import { SessionVaultService } from '@services/session-vault/session-vault.service';
import { TenantConfigService } from '@services/tenant-config/tenant-config.service';
import * as markerjs2 from 'markerjs2';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AlbertaDebugConfigService } from 'src/app/shared/services/alberta-debug/alberta-debug-config.service';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';
import { environment } from 'src/environments/environment';
import { register as registerSwiper } from 'swiper/element/bundle';
import { PipelineRegistry } from './shared/pipeline/start/pipeline-registry';
import { AccountService } from './shared/services/account.service';
import { SentryThrottleService } from './shared/services/sentry-throttle.service';
import { StatusBarService } from './shared/services/statusbar.service';
import { TrackerService } from './shared/services/tracker.service';

registerSwiper();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit {
  public isLocked = false;
  public hasOverrides$: Observable<boolean>;
  /** First rollout of the session status banner: IT-Labs users only, everyone once it has proven itself (#27294). */
  public showSessionStatus$: Observable<boolean>;

  constructor(
    private readonly translate: TranslateService,
    private readonly platform: Platform,
    private readonly appflowLiveUpdatesService: AppflowLiveUpdatesService,
    private readonly environmentService: EnvironmentService,
    private readonly tracker: TrackerService,
    private readonly _statusBarService: StatusBarService,
    private readonly _pipelineRegistry: PipelineRegistry,
    private readonly router: Router,
    private zone: NgZone,
    private sessionVault: SessionVaultService,
    private _tenantConfigService: TenantConfigService,
    private _debugConfig: AlbertaDebugConfigService,
    private _sentryThrottle: SentryThrottleService,
    accountService: AccountService
  ) {
    this.hasOverrides$ = this._debugConfig.hasOverrides$;
    this.showSessionStatus$ = accountService.account$.pipe(
      switchMap(account => (account ? accountService.userIsItLabs() : of(false)))
    );
  }

  ngOnInit(): void {
    this.initializeApp();
    markerjs2.Activator.addKey(environment.markerjs2Key);
  }

  // Dev-only convenience action to clear local config overrides
  public clearOverrides(ev?: Event): void {
    ev?.preventDefault();
    this._debugConfig.clear();
  }

  async initializeApp() {
    this.initTranslate();
    await this.platform.ready();
    this.initDeepLinks();
    await this.appflowLiveUpdatesService.setChannel();

    // Sentry before the start pipeline: the pipeline connects the socket, and with the first socket
    // result the session reads the token, migrates a legacy token and evaluates. Without a Sentry
    // client at that moment, the first start after an update would leave no trace (#27294).
    let dist = Capacitor.isNativePlatform() ? 'app' : 'web';
    dist = dist + '-' + this.environmentService.buildNumber;
    await this.initSentry(dist);
    this.tracker.setVersion(this.environmentService.branch, dist);

    await this._pipelineRegistry.start();

    if (Capacitor.isNativePlatform()) {
      this.initCapacitorPlugins();
      await this.initSessionVaultConfig();
    }
  }

  private initTranslate() {
    this.translate.setDefaultLang('de');
    this.translate.use('de');
  }

  private async initSentry(dist: string) {
    const release = `alberta.mobile@${this.environmentService.version}_${this.environmentService.getCommitHash}`;
    const sentryOptions = {
      dsn: environment.sentry.dsn,
      environment: this.environmentService.branch,
      release,
      dist,
      ignoreErrors: [
        'Twilsock: request timeout',
        'Timeout of 60000ms exceeded calling create on authentication',
        'Timeout of 60000ms exceeded',
        'src/vendor/mobiscroll/js/mobiscroll.custom-4.0.0',
        /Loading Chunk \d*/,
        // sentry
        'Error: Forbidden', // Twilio
        'Error: disconnected Error: disconnected',
        'transition is invalid while previous transition is still in progress',
        'disconnected (status: 0, code: 0)',
        "Can't connect to twilsock",
      ],
      beforeSend: async (event, hint) => this._sentryThrottle.beforeSend(event, hint),
      // The default integrations stay on: the console capture is what turns the `[session]` and
      // `[connectivity]` console lines into the breadcrumb trail we read after a login problem.
    };
    SentryCapacitor.init(sentryOptions, SentryAngular.init);
  }
  private async initCapacitorPlugins() {
    this._statusBarService.initialize();
    await SplashScreen.hide();
    this.appflowLiveUpdatesService.sync();
  }
  // Session Vault Services Emits Lock Events, when Vault is locked.
  private subscribeToSessionVaultLockStatus() {
    this.sessionVault.locked.subscribe(async locked => {
      if (locked && this.platform.is('hybrid') && (await this.sessionVault.canUnlock())) {
        this.isLocked = true;
      } else if (!locked) {
        this.isLocked = false;
      }
    });
  }
  // We recheck the lock status on platform resume, as the lock screen should be shown
  private subscribeToPlatformResumeToCheckLockStatus() {
    this.platform.resume.subscribe(async () => {
      if (!this.sessionVault.vaultInitialized) {
        return;
      }
      if (await this.sessionVault.canUnlock()) {
        this.isLocked = true;
      }
    });
  }

  private async initSessionVaultConfig() {
    const isLockScreenEnabled = await this._tenantConfigService.lockscreenEnabled();
    const lockScreenAfterMillisecondsInBackground =
      await this._tenantConfigService.lockScreenAfterMillisecondsInBackground();
    const shouldHideContentsInBackground = await this._tenantConfigService.shouldHideContentsInBackground();

    if (isLockScreenEnabled) {
      await this.sessionVault.initialize();
      await this.sessionVault.saveSessionInVault(true);
      const canUseBiometrics = await this.sessionVault.canUseBiometrics();
      const canUseSystemPasscode = await this.sessionVault.canUseSystemPasscode();
      this.subscribeToSessionVaultLockStatus();
      this.subscribeToPlatformResumeToCheckLockStatus();
      if (canUseBiometrics) {
        await this.sessionVault.setUnlockMode('BiometricsWithPasscode');
      } else if (canUseSystemPasscode) {
        await this.sessionVault.setUnlockMode('SystemPasscode');
      }
      if (lockScreenAfterMillisecondsInBackground) {
        await this.sessionVault.setLockscreenTimeout(lockScreenAfterMillisecondsInBackground);
      }
    }
    if (shouldHideContentsInBackground) {
      await this.sessionVault.hideContentsInBackground(true);
    }
  }

  private initDeepLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        console.log('start init deeplinks');
        try {
          const url = new URL(event.url);
          const path = url.pathname;
          const protocol = url.protocol;
          // only https deep links; the appauth:// URLs of the sign-in are handled by the AuthClient
          if (!path || protocol !== 'https:') {
            return;
          }
          // a signed-out user is sent to /login with this path as returnUrl by the AuthGuard
          this.router.navigateByUrl(path);
        } catch (error) {
          window.logger.error('app.component appUrlOpen failed to open deep link', error);
        }
      });
    });
  }
}
